import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import HelpMeDecideContent from 'components/common/helpMeDecide/HelpMeDecideContent';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { useQueryParams } from 'global/hooks/useQuery';
import DynamicFormModal from 'components/common/modals/DynamicFormModal';
import { createStyles } from 'components/common/helpMeDecide/commonCriteriaStyles';
import { INITIAL_ANTHEM_BC } from 'global/utils/HelpMeDecideHelper';
import MedicalCriteria1 from 'components/help-me-decide/anthem/GroupMedical/Criteria1';
import MedicalCriteria2 from 'components/help-me-decide/anthem/GroupMedical/Criteria2';
import MedicalCriteria3 from 'components/help-me-decide/anthem/GroupMedical/Criteria3';

export interface HelpMeDecideAnthemProps {
  numberOfMedicalCriteria: number;
}

const HelpMeDecideAnthem = observer(({ numberOfMedicalCriteria }: HelpMeDecideAnthemProps): JSX.Element => {
  const classes = createStyles();
  const history = useHistory();
  //  Query params
  const query = useQueryParams();
  const cartId = query.get('cartId') || null;
  const pLineId = query.get('productLineId') || null;
  const groupId = query.get('groupId') || null;

  // Mobx store
  const {
    helpMeDecideStore: { criteriaIndex, setCriteriaIndex, setCurrentProductLine },
    browsingStore: {
      loading,
      currentProductLineId,
      cart,
      productLines,
      setCurrentProductLineId,
      collectInitialData,
      updateCart,
      setLoading,
    },
    browsingCriteriaModalStore: {
      openBrowsingCriteriaModal,
      closeBrowsingCriteriaModal,
      saveBrowsingCriteria,
      showBrowsingCriteriaModal,
      dynamicForm,
      form,
      loading: bcModalLoading,
    },
    browsingFilterStore: { browsingCriteria, setBrowsingCriteria, resetBcFromNextCriteria },
    domainStore: { domainHeader },
  } = useRootStore();

  useEffect(() => {
    if (cartId && pLineId && groupId) {
      void collectInitialData(groupId, cartId, pLineId as ProductLineId);
      setCurrentProductLine(pLineId as ProductLineId);
      setBrowsingCriteria(INITIAL_ANTHEM_BC);
    }
  }, []);

  const convertedCart = toJS(cart);
  const convertedDomain = toJS(domainHeader);
  const convertedBc = toJS(browsingCriteria);
  const convertedProductLines = productLines.map((line) => toJS(line)).slice();

  const productLineIdFromUrl = (): ProductLineId => {
    return pLineId ? (pLineId as ProductLineId) : 'GroupMedical';
  };

  const getNumberOfCriteria = (): number => {
    switch (pLineId) {
      case 'GroupMedical':
        return numberOfMedicalCriteria;
      default:
        return 0;
    }
  };

  const openBcModal = (): void => {
    const selectedProductLineId = productLineIdFromUrl();

    setCurrentProductLineId(selectedProductLineId);
    const pLine = convertedProductLines.find((line) => line.productLineId === selectedProductLineId);
    void openBrowsingCriteriaModal(
      convertedCart,
      selectedProductLineId,
      pLine?.eStoreBcFormId || pLine?.bcFormId || '',
      convertedBc,
    );
  };

  const saveModalChanges = async (currentBc: Record<string, string>): Promise<void> => {
    if (!currentProductLineId) throw new Error('Modal cannot be opened without currentProductLineId in the store');
    setLoading(true);

    const updatedCart = await saveBrowsingCriteria(currentBc, pLineId ? (pLineId as ProductLineId) : null);
    runInAction(() => {
      updateCart(updatedCart);
      closeBrowsingCriteriaModal();
      setLoading(false);
      history.push(
        `/browsing?groupId=${groupId}&cartId=${cartId}&productLineId=${currentProductLineId}&cartType=browsing&backType=help-me-decide`,
      );
    });
  };

  const changeCriteria = (type: 'BACK' | 'NEXT'): void => {
    const isFirstCriteria = criteriaIndex === 1;
    const isLastCriteria = criteriaIndex === getNumberOfCriteria();

    switch (type) {
      case 'BACK':
        if (!isFirstCriteria) {
          setCriteriaIndex(criteriaIndex - 1);
        } else {
          setBrowsingCriteria({});
          const url = `/popular-plans?groupId=${groupId}&productLineId=${pLineId}&cartId=${cartId}&cartType=popular`;
          history.push(url);
        }
        if (pLineId) resetBcFromNextCriteria(convertedDomain.id, criteriaIndex, pLineId as ProductLineId);
        break;
      case 'NEXT':
        if (isLastCriteria) {
          openBcModal();
        } else {
          setCriteriaIndex(criteriaIndex + 1);
        }
        break;
      default:
        history.goBack();
        break;
    }
  };

  const getCurrentCriteria = (productLineId: ProductLineId | null): JSX.Element => {
    // These props are used to render criteria form
    const commonCriteriaProps = {
      numberOfCriteria: getNumberOfCriteria(),
      minHeight: '53rem',
      onContinue: (): void => {
        changeCriteria('NEXT');
      },
      onBack: (): void => {
        changeCriteria('BACK');
      },
    };

    switch (productLineId) {
      case 'GroupMedical':
        switch (criteriaIndex) {
          case 1:
            return <MedicalCriteria1 {...commonCriteriaProps} />;
          case 2:
            return <MedicalCriteria2 {...commonCriteriaProps} />;
          case 3:
            return <MedicalCriteria3 {...commonCriteriaProps} />;
          default:
            return <></>;
        }
      default:
        return <></>;
    }
  };

  return (
    <>
      <HelpMeDecideContent formContent={getCurrentCriteria(pLineId ? (pLineId as ProductLineId) : null)} />
      <DynamicFormModal
        show={showBrowsingCriteriaModal}
        onClose={(): void => {
          closeBrowsingCriteriaModal();
        }}
        onAction={(): void => {
          void saveModalChanges(convertedBc);
        }}
        actionButtonText="View Plans"
        dynamicForm={dynamicForm}
        form={form}
        loading={bcModalLoading || loading}
        className={classes.bcModal}
      />
    </>
  );
});

export default HelpMeDecideAnthem;
